import React, { useContext, useState } from 'react';
import {
  BubbleButton,
  BubbleFormGroup,
  BubbleInput,
  BubbleTypeahead,
} from '@go-client/components/BubbleUI';
import { FeedArea } from '@go-shared/types/feedTemplate';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { BrandAttribute } from '@go-shared/types/brand';
import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';
import { DailyProgramVenue } from '@go-shared/types/dailyProgram';

interface EventRundownEditorProps {
  feedArea: FeedArea;
  eventRundownId: string;
  onEventRundownUpdated: (updatedFeedArea: FeedArea) => void;
}

export const EventRundownEditor: React.FC<EventRundownEditorProps> = ({
  feedArea,
  onEventRundownUpdated,
}) => {
  const { dailyProgram, property } = useContext(FeedEditorCtx);

  const [startOffset, setStartOffset] = useState(
    Math.abs(feedArea.content?.params?.startDateOffset ?? 0)
  );

  const [endOffset, setEndOffset] = useState(feedArea.content?.params?.endDateOffset ?? 0);
  const [title, setTitle] = useState(feedArea.content?.params?.eventRundownTitle ?? '');
  const [deckPrefix, setDeckPrefix] = useState(feedArea.content?.params?.eventRundownDeckPrefix);
  const [filterAttributes, setFilterAttributes] = useState(
    feedArea.content?.params?.eventRundownFilter?.attributes ?? []
  );
  const [filterVenues, setFilterVenues] = useState(
    feedArea.content?.params?.eventRundownFilter?.venues ?? []
  );
  const [showDeckPrefix, setShowDeckPrefix] = useState(
    feedArea.content?.params?.eventRundownDeckPrefix !== undefined
  );

  if (!dailyProgram) {
    return <div className="p-4">No daily program available</div>;
  }

  return (
    <>
      <div className="bg-eggshell">
        <div>
          <div className="p-4 grid gap-4 grid-cols-2 border-b">
            <BubbleFormGroup label="Title" className="col-span-2">
              <BubbleInput type="text" value={title} onValue={setTitle} block />
            </BubbleFormGroup>
            <BubbleFormGroup label="Days Before">
              <BubbleInput
                type="number"
                className="mr-2"
                inputClassName="bg-white"
                block
                min={0}
                value={startOffset}
                onValue={val => setStartOffset(Math.max(0, val))}
                id="days-before"
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Days After">
              <BubbleInput
                type="number"
                className="mr-2"
                inputClassName="bg-white"
                block
                min={0}
                value={endOffset}
                onValue={val => setEndOffset(Math.max(0, val))}
                id="days-after"
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Filter by Attributes" className="col-span-2">
              <BubbleTypeahead<BrandAttribute>
                placeholder="Search and add attributes..."
                block
                options={(property?.brand.attributes || []).map(attr => ({
                  key: attr._id,
                  item: attr,
                }))}
                value={filterAttributes
                  .map(id => {
                    const attr = property?.brand.attributes.find(a => a._id === id);
                    return attr
                      ? {
                          key: attr._id,
                          refKey: attr._id,
                          item: attr,
                        }
                      : undefined;
                  })
                  .filter((val): val is BubbleTypeaheadValue<BrandAttribute> => !!val)}
                labelExtractor={a => a.name}
                onAdd={val => {
                  if (!filterAttributes.includes(val.refKey)) {
                    setFilterAttributes([...filterAttributes, val.refKey]);
                  }
                }}
                onRemove={val => {
                  setFilterAttributes(filterAttributes.filter(id => id !== val.refKey));
                }}
                onClearAll={() => setFilterAttributes([])}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Filter by Venues" className="col-span-2">
              <BubbleTypeahead<DailyProgramVenue>
                placeholder="Search and add venues..."
                block
                options={(dailyProgram?.venues || []).map(v => ({
                  key: v.templateId,
                  item: v,
                }))}
                value={filterVenues
                  .map(tplId => {
                    const v = dailyProgram?.venues.find(v => v.templateId === tplId);
                    return v
                      ? {
                          key: v.templateId,
                          refKey: v.templateId,
                          item: v,
                        }
                      : undefined;
                  })
                  .filter((val): val is BubbleTypeaheadValue<DailyProgramVenue> => !!val)}
                labelExtractor={v => v.name}
                onAdd={val => {
                  if (!filterVenues.includes(val.refKey)) {
                    setFilterVenues([...filterVenues, val.refKey]);
                  }
                }}
                onRemove={val => {
                  setFilterVenues(filterVenues.filter(tplId => tplId !== val.refKey));
                }}
                onClearAll={() => setFilterVenues([])}
              />
            </BubbleFormGroup>

            <div className="col-span-2">
              <BubbleFormGroup label="Change Deck Prefix?">
                <BubbleInput
                  type="checkbox"
                  className="mr-2"
                  inputClassName="bg-white"
                  checked={showDeckPrefix}
                  onValue={checked => {
                    setShowDeckPrefix(checked);
                    if (!checked) setDeckPrefix(undefined);
                  }}
                  id="deck-prefix-toggle"
                />
              </BubbleFormGroup>
              {showDeckPrefix && (
                <BubbleFormGroup label="Deck Prefix" className="mt-2">
                  <BubbleInput type="text" value={deckPrefix} onValue={setDeckPrefix} block />
                </BubbleFormGroup>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 sticky bottom-0 bg-white border-t">
        <BubbleButton
          variant="navy"
          icon="rotate"
          onClick={() => {
            const hasAttrs = Array.isArray(filterAttributes) && filterAttributes.length > 0;
            const hasVenues = Array.isArray(filterVenues) && filterVenues.length > 0;
            const hasAnyFilter = hasAttrs || hasVenues;

            const { eventRundownFilter: _ignored, ...restParams } = feedArea.content?.params ?? {};

            const updatedParams: any = {
              ...restParams,
              startDateOffset: -Math.abs(startOffset),
              endDateOffset: endOffset,
              eventRundownTitle: title,
              eventRundownDeckPrefix: showDeckPrefix ? deckPrefix ?? '' : undefined,
              ...(hasAnyFilter && {
                eventRundownFilter: {
                  ...(hasAttrs ? { attributes: filterAttributes } : {}),
                  ...(hasVenues ? { venues: filterVenues } : {}),
                },
              }),
            };

            onEventRundownUpdated({
              ...feedArea,
              content: feedArea.content
                ? { ...feedArea.content, params: updatedParams }
                : undefined,
            });
          }}
        >
          Sync Changes to Feed
        </BubbleButton>
      </div>
    </>
  );
};
